'use client';

import { Icon } from '@iconify/react';
import {
  Accordion,
  AccordionItem,
  cn,
  Listbox,
  ListboxItem,
  ListboxSection,
  Tooltip,
  type ListboxProps,
  type ListboxSectionProps,
} from '@nextui-org/react';
import { type Key } from '@react-types/shared';
import React from 'react';
import { Link } from 'react-router-dom';

export enum SidebarItemType {
  Nest = 'nest',
}

export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
};

export type SidebarProps = Omit<ListboxProps<SidebarItem>, 'children'> & {
  items: SidebarItem[];
  isCompact?: boolean;
  hideEndContent?: boolean;
  iconClassName?: string;
  sectionClasses?: ListboxSectionProps['classNames'];
  classNames?: ListboxProps['classNames'];
  defaultSelectedKey: string;
  onSelect?: (key: string) => void;
};

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      isCompact,
      defaultSelectedKey,
      onSelect,
      hideEndContent,
      sectionClasses: sectionClassesProp = {},
      itemClasses: itemClassesProp = {},
      iconClassName,
      classNames,
      className,
      selectedKeys: selectedKeysProp,
      ...props
    },
    ref
  ) => {
    const [selected, setSelected] = React.useState<Key>(defaultSelectedKey);

    const findItemByKey = React.useCallback(
      (itemList: SidebarItem[], key: string): SidebarItem | undefined => {
        for (const entry of itemList) {
          if (entry.key === key) {
            return entry;
          }
          if (entry.items?.length) {
            const nested = findItemByKey(entry.items, key);
            if (nested) {
              return nested;
            }
          }
        }
        return undefined;
      },
      []
    );

    const handleItemAction = React.useCallback(
      (key: Key) => {
        setSelected(key);
        const clickedItem = findItemByKey(items, String(key));
        if (clickedItem?.href) {
          onSelect?.(String(key));
        }
      },
      [findItemByKey, items, onSelect]
    );

    const sectionClasses = {
      ...sectionClassesProp,
      base: cn(sectionClassesProp?.base, 'w-full', {
        'p-0 max-w-[44px]': isCompact,
      }),
      group: cn(sectionClassesProp?.group, {
        'flex flex-col gap-1': isCompact,
      }),
      heading: cn(
        sectionClassesProp?.heading,
        'text-default-500 font-medium text-xs',
        {
          hidden: isCompact,
        }
      ),
    };

    const selectedKeys = React.useMemo(
      () =>
        selectedKeysProp ?? (selected ? new Set([selected]) : new Set<Key>()),
      [selected, selectedKeysProp]
    );

    const itemClasses = {
      ...itemClassesProp,
      base: cn(
        itemClassesProp?.base,
        'px-3 h-11 rounded-lg gap-2',
        'data-[hover=true]:bg-default-100',
        'data-[selected=true]:bg-primary-50 dark:data-[selected=true]:bg-primary-900/20',
        {
          'w-11 h-11 gap-0 p-0': isCompact,
        }
      ),
      title: cn(
        'text-default-700 font-medium text-sm',
        'group-data-[selected=true]:text-primary-500 dark:group-data-[selected=true]:text-primary-400',
        itemClassesProp?.title
      ),
      wrapper: cn('w-full', itemClassesProp?.wrapper),
    };

    const renderNestItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType = item.items && item.items?.length > 0;

        return (
          <ListboxItem
            key={item.key}
            className={cn(
              'group h-auto w-full p-0 data-[hover=true]:bg-default-100',
              {
                'justify-center': isCompact,
              }
            )}
            textValue={item.title}
          >
            {isNestType ? (
              isCompact ? (
                <Tooltip
                  content={
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          className={cn('text-default-500')}
                          icon={item.icon || ''}
                          width={24}
                        />
                        <span className="text-sm font-medium">
                          {item.title}
                        </span>
                      </div>
                      <div className="pl-6">
                        {item.items?.map((subItem) => (
                          <Link
                            key={subItem.key}
                            className="flex items-center gap-2 py-1 text-sm text-default-500 hover:text-primary-500"
                            to={subItem.href || '#'}
                            onClick={() => {
                              if (subItem.href) {
                                onSelect?.(subItem.key);
                              }
                            }}
                          >
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  }
                  placement="right"
                >
                  <div className="flex h-11 w-11 items-center justify-center">
                    <Icon
                      className={cn(
                        'text-default-500',
                        'group-data-[selected=true]:text-primary-500'
                      )}
                      icon={item.icon || ''}
                      width={24}
                    />
                  </div>
                </Tooltip>
              ) : (
                <Accordion className="p-0">
                  <AccordionItem
                    key={item.key}
                    aria-label={item.title}
                    classNames={{
                      heading: 'pr-3',
                      trigger: 'p-0',
                      content: 'py-0 pl-4',
                    }}
                    title={
                      item.icon ? (
                        <div className="flex h-11 items-center gap-2 px-2 py-1.5">
                          <Icon
                            className={cn(
                              'text-default-500',
                              'group-data-[selected=true]:text-primary-500 dark:group-data-[selected=true]:text-primary-400',
                              iconClassName
                            )}
                            icon={item.icon}
                            width={24}
                          />
                          <span className="text-sm font-medium text-default-500 group-data-[selected=true]:text-primary-500 dark:group-data-[selected=true]:text-primary-400">
                            {item.title}
                          </span>
                        </div>
                      ) : (
                        (item.startContent ?? null)
                      )
                    }
                  >
                    {item.items && item.items?.length > 0 ? (
                      <Listbox
                        className="mt-0.5"
                        classNames={{
                          list: cn('border-l border-divider pl-4'),
                        }}
                        items={item.items}
                        variant="flat"
                        onAction={handleItemAction}
                      >
                        {item.items.map(renderItem)}
                      </Listbox>
                    ) : (
                      renderItem(item)
                    )}
                  </AccordionItem>
                </Accordion>
              )
            ) : null}
          </ListboxItem>
        );
      },
      [isCompact, iconClassName, handleItemAction, onSelect]
    );

    const renderItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType =
          item.items &&
          item.items?.length > 0 &&
          item?.type === SidebarItemType.Nest;

        if (isNestType) {
          return renderNestItem(item);
        }

        return (
          <ListboxItem
            {...item}
            key={item.key}
            endContent={
              isCompact || hideEndContent ? null : (item.endContent ?? null)
            }
            startContent={
              isCompact ? null : item.icon ? (
                <Icon
                  className={cn(
                    'text-default-500',
                    'group-data-[selected=true]:text-primary-500 dark:group-data-[selected=true]:text-primary-400',
                    iconClassName
                  )}
                  icon={item.icon}
                  width={24}
                />
              ) : (
                (item.startContent ?? null)
              )
            }
            textValue={item.title}
            title={isCompact ? null : item.title}
          >
            {isCompact ? (
              <Tooltip content={item.title} placement="right">
                <div className="flex w-full items-center justify-center">
                  {item.icon ? (
                    <Icon
                      className={cn(
                        'text-default-500',
                        'group-data-[selected=true]:text-primary-500 dark:group-data-[selected=true]:text-primary-400',
                        iconClassName
                      )}
                      icon={item.icon}
                      width={24}
                    />
                  ) : (
                    (item.startContent ?? null)
                  )}
                </div>
              </Tooltip>
            ) : null}
          </ListboxItem>
        );
      },
      [isCompact, hideEndContent, iconClassName, renderNestItem]
    );

    return (
      <Listbox
        key={isCompact ? 'compact' : 'default'}
        ref={ref}
        hideSelectedIcon
        as="nav"
        className={cn('list-none', className)}
        classNames={{
          ...classNames,
          base: cn('p-0 sm:p-2', classNames?.base),
          list: cn('gap-1', classNames?.list),
        }}
        color="primary"
        itemClasses={itemClasses}
        items={items}
        selectedKeys={selectedKeys}
        selectionMode="single"
        variant="flat"
        onAction={handleItemAction}
        {...props}
      >
        {(item) => {
          return item.items &&
            item.items?.length > 0 &&
            item?.type === SidebarItemType.Nest ? (
            renderNestItem(item)
          ) : item.items && item.items?.length > 0 ? (
            <ListboxSection
              key={item.key}
              classNames={sectionClasses}
              showDivider={isCompact}
              title={item.title}
            >
              {item.items.map(renderItem)}
            </ListboxSection>
          ) : (
            renderItem(item)
          );
        }}
      </Listbox>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export default Sidebar;
